import { useState, useCallback } from 'react';

interface Toast {
  id: number;
  message: string;
  variant: 'success' | 'error' | 'info';
}

let nextId = 0;

export function useToast() {
  const [toasts, setToasts] = useState<Toast[]>([]);

  const show = useCallback((message: string, variant: Toast['variant'] = 'info') => {
    const id = nextId++;
    setToasts(prev => [...prev, { id, message, variant }]);
    setTimeout(() => setToasts(prev => prev.filter(t => t.id !== id)), 3000);
  }, []);

  const success = useCallback((msg: string) => show(msg, 'success'), [show]);
  const error = useCallback((msg: string) => show(msg, 'error'), [show]);

  return { toasts, show, success, error };
}

const COLORS: Record<Toast['variant'], { bg: string; border: string; text: string }> = {
  success: { bg: 'var(--green-bg)', border: 'var(--green-border)', text: 'var(--green)' },
  error: { bg: 'var(--red-bg)', border: 'var(--red-border)', text: 'var(--red)' },
  info: { bg: 'var(--blue-bg)', border: 'var(--blue-border)', text: 'var(--blue)' },
};

export function ToastContainer({ toasts }: { toasts: Toast[] }) {
  if (toasts.length === 0) return null;
  return (
    <div style={{ position: 'fixed', bottom: 20, right: 20, zIndex: 200, display: 'flex', flexDirection: 'column', gap: 8 }}>
      {toasts.map(t => {
        const c = COLORS[t.variant];
        return (
          <div key={t.id} style={{
            padding: '10px 16px', borderRadius: 'var(--radius-md)',
            background: c.bg, border: `1px solid ${c.border}`,
            color: c.text, fontSize: 12, fontWeight: 600,
            boxShadow: '0 4px 12px rgba(0,0,0,0.3)',
            animation: 'fadeUp 0.2s ease',
            maxWidth: 360,
          }}>
            {t.variant === 'success' && '✓ '}{t.variant === 'error' && '⚠ '}{t.message}
          </div>
        );
      })}
    </div>
  );
}
